import React from 'react';
import styled, { css } from 'styled-components/macro';
import { theme } from 'lib/style';
import { MdOutlineCheck } from 'react-icons/md';
import {
  PackageName,
  PackageWithAdditionalInfo,
} from 'lib/const/PackageAdditionalInfo';
import { Button } from 'react-covideo-common';

type WrapperProps = {
  active: boolean;
  simplified: boolean;
};
const Wrapper = styled.div<WrapperProps>`
  position: relative;
  box-shadow:
    0px 0px 4px rgba(66, 79, 104, 0.08),
    0px 6px 12px rgba(66, 79, 104, 0.06);
  border-radius: 6px;
  background: ${theme.palette.white};
  box-sizing: border-box;
  padding: 24px;
  width: 275px;
  min-height: 100px;
  margin-bottom: 16px;
  ${({ active }) =>
    active &&
    css`
      border: 1px solid ${theme.palette.covideoOrange100};
    `}
  ${({ simplified }) =>
    simplified &&
    css`
      width: 200px;
      border: none;
      box-shadow: none;
      padding: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: 0;
      ${FeaturesWrapper} {
        display: none;
      }
      ${BadgeWrapper} {
        display: none;
      }
      ${Badge} {
        background: ${theme.palette.covideoOrange05};
        color: ${theme.palette.covideoOrange100};
      }
      ${Title} {
        font-weight: 700;
        font-size: 18px;
        line-height: 24px;
        margin-bottom: 3px;
      }
      ${Price} {
        margin: 0;
      }
      ${AnnualOffer} {
        display: none;
      }
      ${ButtonWrapper} {
        margin-top: 12px;
      }
    `}
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Title = styled.div`
  font-weight: 800;
  font-size: 24px;
  line-height: 40px;
  color: ${theme.palette.blue100};
`;
const Price = styled.div`
  margin-top: 12px;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: ${theme.palette.covideoOrange100};
  display: flex;
  align-items: baseline;
`;
const AnnualOffer = styled.div`
  display: flex;
  align-items: baseline;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  margin: 8px 0 0 4px;
  color: ${theme.palette.gray60};
`;
const PaymentType = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 24px;
  margin-left: 4px;
  color: ${theme.palette.gray60};
  margin-top: 12px;
  align-items: baseline;
`;
const FeaturesWrapper = styled.div`
  margin-top: 24px;
`;
const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;
const IconWrapper = styled.div`
  flex-shrink: 0;
`;
const FeatureLabel = styled.div`
  margin-left: 12px;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  color: ${theme.palette.gray80};
`;

const BadgeWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Badge = styled.div`
  display: inline-block;
  padding: 2px 8px;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  color: ${theme.palette.white};
  background: ${theme.palette.covideoOrange100};
  border-radius: 5px;
`;

const ButtonWrapper = styled.div`
  margin-top: 24px;
`;

type Props = {
  billingCycle: string;
  currentPackage: number;
  onClickPackage: (id: number, cancel?: boolean) => void;
  packageDetails: PackageWithAdditionalInfo;
  onContactUs: (packageTitle: string) => void;
  simplified?: boolean;
};
export const Card = ({
  packageDetails,
  currentPackage,
  onClickPackage,
  billingCycle,
  onContactUs,
  simplified = false,
}: Props) => {
  const isAnnual = billingCycle === 'annual';

  const getPackagePrice = () => {
    if (isAnnual) {
      return Math.round(packageDetails.feeYearly * 12);
    }

    return packageDetails.feeMonthly;
  };

  return (
    <Wrapper
      active={currentPackage === packageDetails.id}
      simplified={simplified}
    >
      {currentPackage === packageDetails.id && (
        <BadgeWrapper>
          <Badge>Active Plan</Badge>
        </BadgeWrapper>
      )}
      <Header>
        <Title>{packageDetails.title}</Title>
      </Header>
      <Price>
        {currentPackage !== PackageName.LEGACY && packageDetails.showPrice ? (
          <>${getPackagePrice()}</>
        ) : (
          <></>
        )}
        <PaymentType>
          {currentPackage !== PackageName.LEGACY
            ? packageDetails.getLabel(isAnnual)
            : packageDetails.legacyLabel}
        </PaymentType>
      </Price>
      {isAnnual && !!packageDetails.feeYearly && (
        <AnnualOffer>{packageDetails.yearlyOffer} &nbsp;</AnnualOffer>
      )}
      <ButtonWrapper>
        {currentPackage !== PackageName.LEGACY &&
          currentPackage < packageDetails.id &&
          packageDetails.showUpgrade && (
            <Button
              text={
                simplified ? 'Upgrade' : `Upgrade to ${packageDetails.title}`
              }
              variant={
                packageDetails.upgradeButtonOrange ? 'primary' : 'secondary'
              }
              onClick={() => onClickPackage(packageDetails.id)}
            />
          )}
        {currentPackage !== PackageName.LEGACY &&
          currentPackage > packageDetails.id &&
          packageDetails.showDowngrade && (
            <Button
              text={'Downgrade'}
              onClick={() => onClickPackage(packageDetails.id)}
              variant='secondary'
            />
          )}
        {!simplified &&
          currentPackage === packageDetails.id &&
          packageDetails.showCancelSubscription && (
            <Button
              text={'Cancel Subscription'}
              variant='secondary'
              onClick={() => onClickPackage(packageDetails.id, true)}
            />
          )}
        {!simplified &&
          currentPackage === packageDetails.id &&
          packageDetails.showEditSubscription && (
            <Button
              text={'Edit Subscription'}
              variant='secondary'
              onClick={() => onClickPackage(packageDetails.id)}
            />
          )}
        {simplified && currentPackage === packageDetails.id && (
          <Badge>Active Plan</Badge>
        )}
        {((currentPackage !== packageDetails.id &&
          packageDetails.showContact) ||
          currentPackage === PackageName.LEGACY) && (
          <Button
            text={`Contact us`}
            onClick={() => onContactUs(packageDetails.title)}
            variant='ghost'
          />
        )}
      </ButtonWrapper>
      <FeaturesWrapper>
        {packageDetails.mainFeatures.map((feature: any, i: number) => (
          <FeatureRow key={i}>
            <IconWrapper>
              <MdOutlineCheck
                color={theme.palette.covideoOrange100}
                size={18}
              />
            </IconWrapper>
            <FeatureLabel>{feature}</FeatureLabel>
          </FeatureRow>
        ))}
      </FeaturesWrapper>
    </Wrapper>
  );
};
